import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import styles from "./Certificate.module.css";
import CertificateTemplate from "./CertificateTemplate";
import { LanguageContext } from "../LanguageContext/LanguageContext";
import ProfileNavigation from "../ProfileNavigation/ProfileNavigation";
import { useLocation } from "react-router-dom";
import bundlebg from "./Bundle.jpeg";
const lmsUrl = import.meta.env.VITE_LMS_URL;
// Moodle Web Service Configuration
const MOODLE_BASE_URL = `${lmsUrl}`; // Replace with your Moodle URL
const MOODLE_TOKEN = "2a722fa95e3614ef3e297fb6154fd3e8"; // Replace with your Web Service token
const marketingFrontendUrl = import.meta.env.VITE_MARKETING_FRONTEND_URL;
const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;

// Fetch User ID by Email
async function getUserIdByEmail(email) {
  try {
    const response = await axios.post(
      `${MOODLE_BASE_URL}/webservice/rest/server.php`,
      null,
      {
        params: {
          wstoken: MOODLE_TOKEN,
          wsfunction: "core_user_get_users",
          moodlewsrestformat: "json",
          "criteria[0][key]": "email",
          "criteria[0][value]": email,
        },
      }
    );

    const users = response.data.users;
    localStorage.setItem("moodleusername", String(users[0].id));
    localStorage.setItem("currentuserImg", users[0].profileimageurl);
    if (users && users.length > 0) {
      return users[0];
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
}

// Fetch Courses for User
async function getCoursesForUser(userId) {
  try {
    const response = await axios.post(
      `${MOODLE_BASE_URL}/webservice/rest/server.php`,
      null,
      {
        params: {
          wstoken: MOODLE_TOKEN,
          wsfunction: "core_enrol_get_users_courses",
          moodlewsrestformat: "json",
          userid: userId,
          returnusercount: 0,
        },
      }
    );
    return response.data;
  } catch (error) {
    return [];
  }
}

const Certificate = () => {
  const [courseData, setCourseData] = useState([]);
  const [error, setError] = useState(null);
  const [userFullName, setUserFullName] = useState("");
  const [activeTab, setActiveTab] = useState("finished"); // New state for active tab
  const email = localStorage.getItem("useremailidis"); // Replace with actual email logic
  const { direction } = useContext(LanguageContext);
  const isRTL = direction === "rtl";

  const [currentPage, setCurrentPage] = useState({
    inProgress: 1,
    finished: 1,
  });
  const coursesPerPage = 4;
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const [marketingCourses, setMarketingCourses] = useState([]);

  // Add loader effect
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Add effect to fetch marketing courses
  useEffect(() => {
    const fetchMarketingCourses = async () => {
      try {
        const response = await fetch(`${marketingBackendUrl}/maflam/courses`);
        if (!response.ok) throw new Error("Failed to fetch marketing courses");
        const data = await response.json();
        setMarketingCourses(data.courses);
      } catch (error) {
        // Handle error silently
      }
    };

    fetchMarketingCourses();
  }, []);

  // Modify the convertDurationToDays function to better handle the duration string
  const convertDurationToDays = (durationString) => {
    try {
      if (!durationString) return 0;

      // Convert Arabic numerals to English if present
      const arabicToEnglish = (str) => {
        const numerals = {
          "٠": "0",
          "١": "1",
          "٢": "2",
          "٣": "3",
          "٤": "4",
          "٥": "5",
          "٦": "6",
          "٧": "7",
          "٨": "8",
          "٩": "9",
        };
        return str.replace(/[٠-٩]/g, (match) => numerals[match]);
      };

      const normalizedString = arabicToEnglish(durationString);

      // Extract weeks and days using more flexible regex
      const weeksMatch = normalizedString.match(/(\d+)\s*(week|أسبوع)/i);
      const daysMatch = normalizedString.match(/(\d+)\s*(day|يوم)/i);

      const weeks = weeksMatch ? parseInt(weeksMatch[1]) : 0;
      const days = daysMatch ? parseInt(daysMatch[1]) : 0;

      return weeks * 7 + days;
    } catch (error) {
      return 0;
    }
  };

  // Update hasDurationPassed to return more information
  const hasDurationPassed = (startDate, duration) => {
    try {
      if (!startDate || !duration)
        return { passed: false, availableDate: null };

      // Parse the duration string
      const durationMatch = duration.match(
        /(\d+)\s*(week|weeks|أسبوع|أسابيع)/i
      );
      if (!durationMatch) return { passed: false, availableDate: null };

      const numberOfWeeks = parseInt(durationMatch[1]);
      const startDateTime = new Date(startDate * 1000); // Convert UNIX timestamp to Date

      // Add weeks to start date to get available date
      const availableDate = new Date(startDateTime);
      availableDate.setDate(startDateTime.getDate() + numberOfWeeks * 7);

      // Compare with current date
      const currentDate = new Date();

      return {
        passed: currentDate >= availableDate,
        availableDate: availableDate,
      };
    } catch (error) {
      return { passed: false, availableDate: null };
    }
  };

  // Add this helper function to convert numbers to Arabic numerals
  const toArabicNumerals = (num) => {
    const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
    return num
      .toString()
      .split("")
      .map((digit) => arabicNumerals[digit] || digit)
      .join("");
  };

  // Function to format duration based on language
  const formatDurationDisplay = (duration, isRTL) => {
    const durationMatch = duration.match(/(\d+)\s*(week|weeks|أسبوع|أسابيع)/i);
    if (!durationMatch) return duration;

    const weeks = durationMatch[1];

    if (isRTL) {
      const arabicWeeks = toArabicNumerals(weeks);
      return `${arabicWeeks} أسبوع`;
    }
    return `${weeks} ${weeks === "1" ? "Week" : "Weeks"}`;
  };

  // Modify the duration formatting function
  const formatDuration = (durationString, isRTL) => {
    const weeks = (durationString.match(/(\d+)\s*week/i) || [0, 0])[1];
    const days = (durationString.match(/(\d+)\s*day/i) || [0, 0])[1];

    if (isRTL) {
      let duration = "";
      if (weeks > 0) duration += `${weeks} أسبوع`;
      if (days > 0) duration += weeks > 0 ? ` و ${days} يوم` : `${days} يوم`;
      return duration;
    } else {
      let duration = "";
      if (weeks > 0) duration += `${weeks} ${weeks === 1 ? "week" : "weeks"}`;
      if (days > 0)
        duration +=
          weeks > 0
            ? ` and ${days} ${days === 1 ? "day" : "days"}`
            : `${days} ${days === 1 ? "day" : "days"}`;
      return duration;
    }
  };

  // Modify the getAvailableDate function
  const getAvailableDate = (startDate, duration) => {
    try {
      if (!startDate || !duration) {
        throw new Error("Missing startDate or duration");
      }

      // Parse the duration string
      const durationMatch = duration.match(
        /(\d+)\s*(week|weeks|أسبوع|أسابيع)/i
      );
      if (!durationMatch) {
        throw new Error("Invalid duration format");
      }

      const numberOfWeeks = parseInt(durationMatch[1]);
      const startDateTime = new Date(startDate * 1000); // Convert UNIX timestamp to Date

      // Add weeks to start date
      const availableDate = new Date(startDateTime);
      availableDate.setDate(startDateTime.getDate() + numberOfWeeks * 7);

      // Calculate remaining days
      const currentDate = new Date();
      const remainingTime = availableDate.getTime() - currentDate.getTime();
      const remainingDays = Math.ceil(remainingTime / (1000 * 60 * 60 * 24));

      // Format the date based on locale
      if (isRTL) {
        const options = {
          year: "numeric",
          month: "long",
          day: "numeric",
          numberingSystem: "arab",
        };
        const formattedDate = new Intl.DateTimeFormat("ar", options).format(
          availableDate
        );
        return remainingDays > 0
          ? `متاح في ${formattedDate} (متبقي ${remainingDays} يوم)`
          : `متاح الآن`;
      } else {
        const options = {
          year: "numeric",
          month: "long",
          day: "numeric",
        };
        const formattedDate = new Intl.DateTimeFormat("en-US", options).format(
          availableDate
        );
        return remainingDays > 0
          ? `Available on ${formattedDate} (${remainingDays} days remaining)`
          : `Available now`;
      }
    } catch (error) {
      return isRTL ? "تاريخ غير متوفر" : "Date not available";
    }
  };

  useEffect(() => {
    const fetchCourseDataByEmail = async (email) => {
      try {
        const user = await getUserIdByEmail(email);
        if (user) {
          setUserFullName(user.fullname);
          const data = await getCoursesForUser(user.id);

          const transformedData = data.map((course) => {
            const marketingCourse = marketingCourses.find(
              (mc) => mc.CourseNameArabic.trim() === course.shortname.trim()
            );

            const durationCheck =
              marketingCourse && course.progress === 100
                ? hasDurationPassed(
                    course.startdate,
                    marketingCourse.courseDuration
                  )
                : { passed: false, availableDate: null };

            return {
              thumbnail: course.courseimage,
              courseName: course.fullname,
              shortname: course.shortname,
              progress:
                typeof course.progress === "number"
                  ? course.progress.toFixed(2)
                  : "0.00",
              startDate: new Date(course.startdate * 1000).toLocaleDateString(),
              isCompleted:
                typeof course.progress === "number" && course.progress === 100,
              id: course.id,
              marketingData: marketingCourse || null,
              canDownloadCertificate: durationCheck.passed,
              availableDate: durationCheck.availableDate,
              downloadCertificate: () => {
                if (durationCheck.passed) {
                  const link = document.createElement("a");
                  link.href = `/path/to/certificate/${course.id}`;
                  link.download = `${course.shortname}_Certificate.pdf`;
                  link.click();
                }
              },
            };
          });

          transformedData.sort((a, b) =>
            a.courseName.localeCompare(b.courseName, "ar")
          );
          setCourseData(transformedData);
        }
      } catch (err) {
        setError(err.message);
      }
    };

    if (email && marketingCourses.length > 0) {
      fetchCourseDataByEmail(email);
    }
  }, [email, marketingCourses]);

  // Separate pagination for each tab
  const getCurrentPageForTab = () => currentPage[activeTab];

  const getPageData = (courses) => {
    const currentPageNumber = getCurrentPageForTab();
    const indexOfLastCourse = currentPageNumber * coursesPerPage;
    const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
    return courses.slice(indexOfFirstCourse, indexOfLastCourse);
  };

  // Filter all courses first
  const allFinishedCourses = courseData.filter((course) => course.isCompleted);
  const allCoursesInProgress = courseData.filter(
    (course) => !course.isCompleted
  );

  // Calculate overall progress percentage for all courses
  const calculateOverallProgress = () => {
    if (courseData.length === 0) return 0;

    const totalProgress = courseData.reduce((sum, course) => {
      return sum + (parseFloat(course.progress) || 0);
    }, 0);

    return (totalProgress / courseData.length).toFixed(2);
  };

  const overallProgress = calculateOverallProgress();

  // Add Full Bundle Package certificate if there are more than 10 total courses
  const showBundleCertificate = courseData.length > 10;
  // We'll keep this variable for UI display purposes only, not for blocking downloads
  const allCoursesCompleted =
    showBundleCertificate && allCoursesInProgress.length === 0;

  // Add this test function near the top of the component
  const testAllCoursesCompleted = () => {
    console.log("Bundle Certificate Status:", {
      totalCourses: courseData.length,
      completedCourses: allFinishedCourses.length,
      inProgressCourses: allCoursesInProgress.length,
      isAllCompleted: allCoursesCompleted,
      overallProgress,
    });
  };

  // Update the bundle certificate object
  const bundleCertificate = showBundleCertificate
    ? {
        thumbnail: bundlebg,
        courseName: "الباقة الكاملة لأساسيات صناعة الأفلام",
        shortname: "الباقة الكاملة لأساسيات صناعة الأفلام",
        progress: overallProgress,
        startDate: new Date().toLocaleDateString(),
        isCompleted: 1,
        id: "bundle-certificate",
        marketingData: null,
        canDownloadCertificate: allCoursesCompleted,
        availableDate: new Date(),
        // Add remaining courses count for easy access
        remainingCourses: allCoursesInProgress.length,
      }
    : null;

  useEffect(() => {
    // Log certificate status whenever course data changes
    if (showBundleCertificate) {
      testAllCoursesCompleted();
    }
  }, [courseData]);

  // Update the lists to exclude bundle certificate
  const finishedCoursesWithBundle = allFinishedCourses;
  const inProgressCoursesWithBundle = allCoursesInProgress;

  // Get current page's courses based on active tab
  const currentCourses =
    activeTab === "inProgress"
      ? getPageData(inProgressCoursesWithBundle)
      : getPageData(finishedCoursesWithBundle);

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      const currentPageNumber = prev[activeTab];
      const totalCourses =
        activeTab === "inProgress"
          ? inProgressCoursesWithBundle.length
          : finishedCoursesWithBundle.length;
      const totalPages = Math.ceil(totalCourses / coursesPerPage);

      let newPage =
        direction === "next"
          ? Math.min(currentPageNumber + 1, totalPages)
          : Math.max(currentPageNumber - 1, 1);

      return {
        ...prev,
        [activeTab]: newPage,
      };
    });
  };

  // Reset page when switching tabs
  useEffect(() => {
    setCurrentPage((prev) => ({
      ...prev,
      [activeTab]: 1,
    }));
  }, [activeTab]);

  const handleDownloadCertificate = (course) => {
    // For bundle certificate, don't need a custom handler anymore as we're using CertificateTemplate
    // For regular courses, keep the existing check
    if (course.isCompleted && course.id !== "bundle-certificate") {
      course.downloadCertificate();
    }
  };

  if (error) {
    return <p>Error: {error}</p>;
  }

  const getTotalPages = () => {
    const totalCourses =
      activeTab === "inProgress"
        ? inProgressCoursesWithBundle.length
        : finishedCoursesWithBundle.length;
    return Math.ceil(totalCourses / coursesPerPage);
  };

  return (
    <div
      className={styles.container}
      style={{ direction: isRTL ? "rtl" : "ltr" }}
    >
      {isLoading && (
        <div className={styles.loaderWrapper}>
          <span className={styles.loader}></span>
        </div>
      )}
      <ProfileNavigation />

      {/* Add Bundle Certificate Section above tabs if available */}
      {showBundleCertificate && bundleCertificate && (
        <div className={styles.courseCard} style={{ marginBottom: "2rem" }}>
          <div className={styles.leftSection}>
            <div className={styles.certificateIcon}>
              <span
                style={{
                  color: allCoursesCompleted ? "rgb(57,255,251)" : "#fff",
                  fontSize: "1.25rem",
                  fontWeight: "700",
                }}
              >
                {isRTL
                  ? "الباقة الكاملة لأساسيات صناعة الأفلام"
                  : "Filmmaking Fundamentals Bundle"}
              </span>
              <div className={styles.certificateAction}>
                {allCoursesCompleted ? (
                  <CertificateTemplate
                    data={{
                      name: userFullName,
                      courseName: bundleCertificate.shortname,
                    }}
                  />
                ) : (
                  <div
                    className={styles.certificateWaiting}
                    style={{ padding: "1rem", textAlign: "center" }}
                  >
                    <span
                      style={{
                        color: "#fff",
                        fontSize: "0.875rem",
                        marginBottom: "8px",
                        fontWeight: "500",
                        display: "block",
                      }}
                    >
                      {isRTL
                        ? `يجب إكمال جميع الدورات للحصول على شهادة الحزمة الكاملة`
                        : `Complete all courses to get the Full Bundle Certificate`}
                    </span>
                    <span
                      style={{
                        color: "white",
                        fontSize: "0.8rem",
                        opacity: "0.9",
                        display: "block",
                        marginTop: "0.5rem",
                      }}
                    >
                      {isRTL
                        ? `${bundleCertificate.remainingCourses} دورات متبقية`
                        : `${bundleCertificate.remainingCourses} courses remaining`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.rightSection}>
            <div className={styles.instructorImageContainer}>
              <img
                src={bundleCertificate.thumbnail}
                alt={bundleCertificate.courseName}
                className={styles.instructorImage}
              />
            </div>

            <div
              className={styles.courseInfoContainer}
              style={{
                background: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${bundleCertificate.thumbnail}) center / cover`,
              }}
            >
              <div className={styles.courseInfo}>
                <h2 className={styles.courseName}>
                  {bundleCertificate.courseName}
                </h2>
              </div>

              <div className={styles.progressSection}>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{
                      width: `${bundleCertificate.progress}%`,
                      background: allCoursesCompleted
                        ? "linear-gradient(90deg, #39FFFB 0%, #00BFFF 100%)"
                        : "linear-gradient(90deg, #FFD700 0%, #FFA500 100%)",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <span className={styles.progressPercentage}>
                    {bundleCertificate.progress}%
                  </span>
                  <span className={styles.progressLabel}>
                    {isRTL ? "التقدم الإجمالي" : "Overall Progress"}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#39FFFB",
                    marginTop: "5px",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {isRTL
                    ? `${courseData.length} دورة متوفرة`
                    : `${courseData.length} Courses Available`}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.tabContainer}>
        <button
          className={`${styles.tabButton} ${
            activeTab === "finished" ? styles.activeTab : ""
          }`}
          onClick={() => setActiveTab("finished")}
        >
          {isRTL ? "الدورات المكتملة" : "Finished Courses"}
        </button>
        <button
          className={`${styles.tabButton} ${
            activeTab === "inProgress" ? styles.activeTab : ""
          }`}
          onClick={() => setActiveTab("inProgress")}
        >
          {isRTL ? "الدورات قيد التقدم" : "Courses in Progress"}
        </button>
      </div>

      <div className={styles.courseContainer}>
        {activeTab === "inProgress"
          ? currentCourses.map((course, index) => (
              <div key={index} className={styles.courseCard}>
                <div className={styles.leftSection}>
                  <div className={styles.certificateIcon}>
                    <span
                      style={{
                        color: "rgb(57,255,251)",
                        fontSize: "1.25rem",
                        fontWeight: "700",
                      }}
                    >
                      {isRTL ? "قيد التقدم" : "In Progress"}
                    </span>
                  </div>
                </div>

                <div className={styles.rightSection}>
                  <div className={styles.instructorImageContainer}>
                    <img
                      src={course.thumbnail}
                      alt={course.courseName}
                      className={styles.instructorImage}
                    />
                  </div>

                  <div
                    className={styles.courseInfoContainer}
                    style={{
                      background: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${course.thumbnail}) center / cover`,
                    }}
                  >
                    <div className={styles.courseInfo}>
                      <h2 className={styles.courseName}>
                        <a
                          href={`${lmsUrl}/course/view.php?id=${course.id}&lang=ar`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "white",
                            textDecoration: "none",
                            cursor: "pointer",
                            fontSize: "1.25rem",
                          }}
                        >
                          {course.courseName}
                        </a>
                      </h2>
                    </div>

                    <div className={styles.progressSection}>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{
                            width: `${course.progress}%`,
                            background:
                              course.id === "bundle-certificate"
                                ? allCoursesCompleted
                                  ? "linear-gradient(90deg, #39FFFB 0%, #00BFFF 100%)"
                                  : "linear-gradient(90deg, #FFD700 0%, #FFA500 100%)"
                                : undefined,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <span className={styles.progressPercentage}>
                          {course.progress}%
                        </span>
                        <span className={styles.progressLabel}>
                          {course.id === "bundle-certificate"
                            ? isRTL
                              ? "التقدم الإجمالي"
                              : "Overall Progress"
                            : isRTL
                            ? "التقدم الإجمالي"
                            : "Overall Progress"}
                        </span>
                      </div>
                      {course.id === "bundle-certificate" && (
                        <div
                          style={{
                            fontSize: "0.8rem",
                            color: "#39FFFB",
                            marginTop: "5px",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {isRTL
                            ? `${courseData.length} دورة متوفرة - شهادة متاحة للتحميل فوراً`
                            : `${courseData.length} Courses Available - Certificate Ready for Download`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          : currentCourses.map((course, index) => (
              <div key={index} className={styles.courseCard}>
                <div className={styles.leftSection}>
                  <div className={styles.certificateIcon}>
                    {course.isCompleted ? (
                      course.id === "bundle-certificate" ? (
                        <>
                          <span
                            style={{
                              color: course.canDownloadCertificate
                                ? "rgb(57,255,251)"
                                : "#FFE600",
                              fontSize: "1.25rem",
                              fontWeight: "700",
                            }}
                          >
                            {course.canDownloadCertificate
                              ? isRTL
                                ? "احصل على الشهادة"
                                : "Get the Certificate"
                              : isRTL
                              ? ""
                              : " "}
                          </span>
                          <div className={styles.certificateAction}>
                            {course.canDownloadCertificate ? (
                              <>
                                <CertificateTemplate
                                  data={{
                                    name: userFullName,
                                    courseName: course.shortname,
                                  }}
                                />
                              </>
                            ) : (
                              <div
                                className={styles.certificateWaiting}
                                style={{ padding: "1rem", textAlign: "center" }}
                              >
                                <span
                                  style={{
                                    color: "#FFE600",
                                    fontSize: "0.875rem",
                                    marginBottom: "8px",
                                    fontWeight: "500",
                                    display: "block",
                                  }}
                                >
                                  {isRTL
                                    ? `يجب إكمال جميع الدورات للحصول على شهادة الحزمة الكاملة`
                                    : `Complete all courses to get the Full Bundle Certificate`}
                                </span>
                                <span
                                  style={{
                                    color: "white",
                                    fontSize: "0.8rem",
                                    opacity: "0.9",
                                    display: "block",
                                    marginTop: "0.5rem",
                                  }}
                                >
                                  {isRTL
                                    ? `${course.remainingCourses} دورات متبقية`
                                    : `${course.remainingCourses} courses remaining`}
                                </span>
                                <div
                                  style={{
                                    marginTop: "0.5rem",
                                    fontSize: "0.8rem",
                                    color: "#39FFFB",
                                  }}
                                >
                                  {isRTL
                                    ? `التقدم الإجمالي: ${course.progress}%`
                                    : `Overall Progress: ${course.progress}%`}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      ) : course.canDownloadCertificate ? (
                        <>
                          <span
                            style={{
                              color: "rgb(57,255,251)",
                              fontSize: "1.25rem",
                              fontWeight: "700",
                            }}
                          >
                            {isRTL ? "احصل على الشهادة" : "Get the Certificate"}
                          </span>
                          <div className={styles.certificateAction}>
                            {course.id === "bundle-certificate" ? (
                              <button
                                className={styles.downloadButton}
                                onClick={() =>
                                  handleDownloadCertificate(course)
                                }
                                style={{
                                  color: "black",
                                  background: "rgb(57,255,251)",
                                  padding: "8px 16px",
                                  borderRadius: "4px",
                                  border: "none",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M8 11L3 6L4.4 4.6L7 7.175V0H9V7.175L11.6 4.6L13 6L8 11Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M13 14H3C2.73478 14 2.48043 13.8946 2.29289 13.7071C2.10536 13.5196 2 13.2652 2 13V10H4V12H12V10H14V13C14 13.2652 13.8946 13.5196 13.7071 13.7071C13.5196 13.8946 13.2652 14 13 14Z"
                                    fill="currentColor"
                                  />
                                </svg>
                                {isRTL
                                  ? "تحميل الشهادة"
                                  : "Download Certificate"}
                              </button>
                            ) : (
                              <CertificateTemplate
                                data={{
                                  name: userFullName,
                                  courseName: course.shortname,
                                }}
                              />
                            )}
                          </div>
                        </>
                      ) : (
                        <div className={styles.certificateWaiting}>
                          <span
                            style={{
                              color: "#FFE600",
                              fontSize: "0.875rem",
                              marginBottom: "8px",
                              fontWeight: "500",
                            }}
                          >
                            {isRTL
                              ? `يرجى إكمال الحد الأدنى (${formatDurationDisplay(
                                  course.marketingData.courseDuration,
                                  true
                                )})`
                              : `Please Complete Minimum Duration (${formatDurationDisplay(
                                  course.marketingData.courseDuration,
                                  false
                                )})`}
                          </span>
                          {course.marketingData?.courseDuration && (
                            <span
                              style={{
                                color: "white",
                                fontSize: "0.8rem",
                                opacity: "0.9",
                              }}
                            >
                              {course.availableDate
                                ? isRTL
                                  ? `سيكون متاحاً في: ${new Intl.DateTimeFormat(
                                      "ar",
                                      {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                        numberingSystem: "arab",
                                      }
                                    ).format(course.availableDate)}`
                                  : `Will Be Available on: ${new Intl.DateTimeFormat(
                                      "en-US",
                                      {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                      }
                                    ).format(course.availableDate)}`
                                : isRTL
                                ? "تاريخ غير متوفر"
                                : "Date Not Available"}
                            </span>
                          )}
                        </div>
                      )
                    ) : null}
                  </div>
                </div>

                <div className={styles.rightSection}>
                  <div className={styles.instructorImageContainer}>
                    <img
                      src={course.thumbnail}
                      alt={course.courseName}
                      className={styles.instructorImage}
                    />
                  </div>

                  <div
                    className={styles.courseInfoContainer}
                    style={{
                      background: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${course.thumbnail}) center / cover`,
                    }}
                  >
                    <div className={styles.courseInfo}>
                      <h2 className={styles.courseName}>
                        <a
                          href={`${lmsUrl}/course/view.php?id=${course.id}&lang=ar`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "white",
                            textDecoration: "none",
                            cursor: "pointer",
                            fontSize: "1.25rem",
                          }}
                        >
                          {course.courseName}
                        </a>
                      </h2>
                    </div>

                    <div className={styles.progressSection}>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{
                            width: `${course.progress}%`,
                            background:
                              course.id === "bundle-certificate"
                                ? allCoursesCompleted
                                  ? "linear-gradient(90deg, #39FFFB 0%, #00BFFF 100%)"
                                  : "linear-gradient(90deg, #FFD700 0%, #FFA500 100%)"
                                : undefined,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <span className={styles.progressPercentage}>
                          {course.progress}%
                        </span>
                        <span className={styles.progressLabel}>
                          {course.id === "bundle-certificate"
                            ? isRTL
                              ? "التقدم الإجمالي"
                              : "Overall Progress"
                            : isRTL
                            ? "التقدم الإجمالي"
                            : "Overall Progress"}
                        </span>
                      </div>
                      {course.id === "bundle-certificate" && (
                        <div
                          style={{
                            fontSize: "0.8rem",
                            color: "#39FFFB",
                            marginTop: "5px",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        ></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
      </div>

      <div className={styles.pagination}>
        <button
          onClick={() => handlePageChange("prev")}
          disabled={getCurrentPageForTab() === 1}
        >
          {isRTL ? "السابق" : "Previous"}
        </button>
        <span>
          {getCurrentPageForTab()} / {getTotalPages()}
        </span>
        <button
          onClick={() => handlePageChange("next")}
          disabled={getCurrentPageForTab() === getTotalPages()}
        >
          {isRTL ? "التالي" : "Next"}
        </button>
      </div>
    </div>
  );
};

export default Certificate;
